import careConfig from "@careConfig";
import { useTranslation } from "react-i18next";

const RecordRow = ({
  icon,
  label,
}: {
  icon: React.ReactNode;
  label: string;
}) => (
  <div className="flex items-center gap-2.5">
    <span className="flex size-6 flex-none items-center justify-center rounded-lg border border-primary-400/30 bg-primary-400/15">
      {icon}
    </span>
    <span className="flex-1 text-[12.5px] font-medium text-slate-100">
      {label}
    </span>
    <span className="flex flex-none text-primary-400">
      <svg
        width="14"
        height="14"
        viewBox="0 0 14 14"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M3 7.2 5.7 10 11 3.8"
          stroke="currentColor"
          strokeWidth="1.6"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </span>
  </div>
);

export const AuthHero = () => {
  const { stateLogo, customLogo } = careConfig;
  const customDescriptionHtml = __CUSTOM_DESCRIPTION_HTML__;
  const { t } = useTranslation();

  const logos = [stateLogo, customLogo].filter(
    (logo) => logo?.light || logo?.dark,
  );

  return (
    <div className="login-hero relative order-last flex flex-auto flex-col justify-between overflow-hidden p-6 md:order-first md:h-full md:w-[calc(50%+130px)] md:flex-none md:justify-start md:p-10 md:px-16 md:pr-[calc(4rem+130px)]">
      <div className="relative z-[2] flex items-center gap-2.5">
        {logos.length > 0 ? (
          logos.map((logo, index) =>
            logo && logo.light ? (
              <div key={index} className="flex items-center">
                <img
                  src={logo.light}
                  className="h-10 rounded-lg"
                  alt="state logo"
                />
              </div>
            ) : null,
          )
        ) : (
          <div className="flex items-center gap-2.5">
            <span className="flex size-8 flex-none items-center justify-center rounded-[9px] border border-primary-400/40 bg-primary-400/15">
              <svg
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M1 9h3l1.5-4L8 13l1.5-7L11 9h4"
                  stroke="#D89B3C"
                  strokeWidth="1.4"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </span>
            <span className="text-[19px] font-bold tracking-tight text-white">
              {t("wardline_brand_name")}
            </span>
          </div>
        )}
      </div>

      <div className="relative z-[2] mt-auto mb-6 md:mt-16">
        <div className="relative max-w-[280px] rounded-2xl border border-white/10 bg-gradient-to-br from-white/[0.06] to-white/[0.02] p-4 pb-[18px] shadow-[0_24px_44px_-22px_rgba(0,0,0,0.55)] backdrop-blur-xl">
          <div className="mb-3.5 flex items-center gap-2">
            <span className="size-1.5 flex-none animate-pulse rounded-full bg-primary-400" />
            <span className="text-[12.5px] font-semibold text-white/90">
              {t("wardline_unified_record")}
            </span>
            <span className="ml-auto rounded-full border border-primary-400/40 px-2.5 py-[3px] text-[10.5px] font-semibold text-primary-100">
              {t("wardline_synced")}
            </span>
          </div>
          <div className="flex flex-col gap-2.5">
            <RecordRow
              label={t("wardline_outpatient_visit")}
              icon={
                <svg
                  width="13"
                  height="13"
                  viewBox="0 0 13 13"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M2 3.5h9M2 6.5h9M2 9.5h6"
                    stroke="#D89B3C"
                    strokeWidth="1.3"
                    strokeLinecap="round"
                  />
                </svg>
              }
            />
            <RecordRow
              label={t("wardline_lab_results")}
              icon={
                <svg
                  width="13"
                  height="13"
                  viewBox="0 0 13 13"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M5 1.5v3.2L2.3 9.8a1.3 1.3 0 0 0 1.15 1.9h6.1a1.3 1.3 0 0 0 1.15-1.9L8 4.7V1.5"
                    stroke="#D89B3C"
                    strokeWidth="1.2"
                    strokeLinejoin="round"
                  />
                </svg>
              }
            />
            <RecordRow
              label={t("wardline_pharmacy_orders")}
              icon={
                <svg
                  width="13"
                  height="13"
                  viewBox="0 0 13 13"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <rect
                    x="2"
                    y="2.5"
                    width="9"
                    height="8"
                    rx="1.3"
                    stroke="#D89B3C"
                    strokeWidth="1.2"
                  />
                  <path
                    d="M4.3 6.5h4.4M6.5 4.3v4.4"
                    stroke="#D89B3C"
                    strokeWidth="1.2"
                    strokeLinecap="round"
                  />
                </svg>
              }
            />
            <RecordRow
              label={t("wardline_billing_summary")}
              icon={
                <svg
                  width="13"
                  height="13"
                  viewBox="0 0 13 13"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <rect
                    x="2"
                    y="2"
                    width="9"
                    height="9"
                    rx="1.3"
                    stroke="#D89B3C"
                    strokeWidth="1.2"
                  />
                  <path
                    d="M4.3 5h4.4M4.3 7.3h3"
                    stroke="#D89B3C"
                    strokeWidth="1.2"
                    strokeLinecap="round"
                  />
                </svg>
              }
            />
          </div>
        </div>
      </div>

      <div className="relative z-[2] max-w-lg">
        <h1 className="mb-4 max-w-[420px] text-[38px] font-bold leading-[1.05] tracking-tight text-white lg:text-[48px]">
          {t("wardline_hero_title")}
        </h1>
        {customDescriptionHtml ? (
          <div
            className="max-w-xl text-[15.5px] leading-relaxed text-secondary-400"
            dangerouslySetInnerHTML={{
              __html: __CUSTOM_DESCRIPTION_HTML__,
            }}
          />
        ) : (
          <p className="max-w-[400px] text-[15.5px] leading-relaxed text-secondary-400">
            {t("wardline_hero_tagline")}
          </p>
        )}
      </div>

      <div className="relative z-[2] mt-10">
        <div className="mb-4 flex flex-wrap gap-2">
          {[
            "wardline_chip_fhir_ready",
            "wardline_chip_role_based_access",
            "wardline_chip_multi_facility",
          ].map((key) => (
            <span
              key={key}
              className="rounded-full border border-primary-400/35 bg-primary-400/10 px-2.5 py-[5px] text-xs font-medium text-primary-100"
            >
              {t(key)}
            </span>
          ))}
        </div>

        <p className="max-w-[420px] border-l-2 border-primary-400 bg-white/[0.03] py-3 pl-3.5 text-[13px] leading-[1.55] text-secondary-500">
          {t("wardline_instance_note")}
        </p>
      </div>
    </div>
  );
};
